import { useState } from 'react';
import { CheckinForm } from '../components/CheckinForm';
import { DailyGoodThing } from '../components/DailyGoodThing';
import { GraduationForm } from '../components/GraduationForm';
import type { MeResponse } from '../lib/api';

const ACTIVE_COMMUNITY_STORAGE_KEY = 'la-active-community';

/**
 * "You" / Reflections — a private space that's never shared with a match. It
 * gives the daily good-thing, the weekly UCLA-3 check-in, and "stepping away
 * well" a calm home of their own instead of being buried on the dashboard.
 * Surfaced by the "Reflections" rail item (desktop) and the "You" tab (native).
 *
 * Composes the existing ritual components. The trend mini-chart and "rhythm"
 * stat tiles from the concept are deferred enhancements (they need a check-in
 * history endpoint) — the write box, history, check-in, and graduation are the
 * load-bearing core and ship now.
 */
export function Reflections({ me }: { me: MeResponse }) {
  const membership = (() => {
    const ms = me.memberships;
    const first = ms[0];
    if (!first) return undefined;
    try {
      const stored = window.localStorage.getItem(ACTIVE_COMMUNITY_STORAGE_KEY);
      if (stored) {
        const m = ms.find((x) => x.communityId === stored);
        if (m) return m;
      }
    } catch {
      // ignore
    }
    return first;
  })();

  const [checkin, setCheckin] = useState<'idle' | 'open' | 'done'>('idle');
  const [graduation, setGraduation] = useState<'idle' | 'open' | 'done'>('idle');

  if (!membership) return null;

  if (graduation === 'done') {
    return (
      <main className="reflections">
        <div className="card">
          <span className="step-label">{membership.communityName}</span>
          <h1>Take care, {membership.nickname}.</h1>
          <p>
            Your honest check-in is recorded and your matches have been told a fresh pairing is
            coming. Stepping away when you feel better is the whole point.
          </p>
          <p className="muted">
            The same university email signs you back in any time. And if you're ever in crisis, you
            can call or text <strong>988</strong>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="reflections">
      <header className="you-head">
        <span className="step-label you-eyebrow">Your private space</span>
        <h1>Reflections</h1>
        <p className="you-sub">
          A quiet record that's just for you — never shared with your match. A small note when
          something goes well, and a weekly check-in to see how you're really doing.
        </p>
      </header>

      <div className="you-grid">
        {/* Left — write + history */}
        <section className="you-main">
          <div className="home-card is-hero">
            <DailyGoodThing />
          </div>
        </section>

        {/* Right — check-in + stepping away */}
        <aside className="you-side">
          {checkin === 'idle' ? (
            <div className="ci-card">
              <span className="eyebrow">Weekly check-in</span>
              <h3>How connected have you felt?</h3>
              <p className="muted">
                Three quiet questions, the same each week — it's how the pilot knows whether this is
                helping. Only you and the team ever see it.
              </p>
              <button type="button" className="ci-go" onClick={() => setCheckin('open')}>
                Start this week's check-in
              </button>
            </div>
          ) : (
            <div className="home-card">
              <h2>Weekly check-in</h2>
              {checkin === 'done' && (
                <p className="muted">
                  Thanks — your check-in is recorded. Taking it now and then is how we see whether
                  LessAlone is helping.
                </p>
              )}
              {checkin === 'open' && (
                <CheckinForm checkpoint="ongoing" onDone={() => setCheckin('done')} />
              )}
            </div>
          )}

          <div className="grad-card">
            <span className="eyebrow">When you're ready</span>
            <h3>Step away well</h3>
            {graduation === 'open' ? (
              <GraduationForm
                communityId={membership.communityId}
                onDone={() => setGraduation('done')}
                onCancel={() => setGraduation('idle')}
              />
            ) : (
              <>
                <p>
                  Feeling better is the goal — not a reason to feel guilty. Closing well lets your
                  match hear it as a small win, not a disappearance.
                </p>
                <button type="button" className="grad-link" onClick={() => setGraduation('open')}>
                  Step away well →
                </button>
              </>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}
